import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import type { ReactNode } from 'react';

interface StatWidgetProps {
  label: string;
  value: string | number;
  color?: string;
  icon?: ReactNode;
  hint?: string;
}

/** Glowing exchange-style metric tile with an accent icon chip. */
export function StatWidget({ label, value, color = '#4f8cff', icon, hint }: StatWidgetProps) {
  return (
    <Card
      sx={{
        flex: '1 1 200px',
        minWidth: 200,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: alpha(color, 0.5),
          boxShadow: `0 12px 30px ${alpha(color, 0.18)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(120px 80px at 100% 0%, ${alpha(color, 0.16)}, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              color,
              backgroundColor: alpha(color, 0.14),
              border: `1px solid ${alpha(color, 0.3)}`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5, color, fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {hint && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {hint}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
